import React, { useMemo } from 'react';
import { useI18nContext } from '@bingads-webui/copilot-i18n';
import {
  Button,
  Label,
  ProgressBar,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  PersonSupport24Regular,
  CheckmarkCircle20Filled,
  SquareDismiss20Regular,
} from '@fluentui/react-icons';
import { LiveAgentConnectionState } from '../helpers';

interface ILiveAgentBannerBaseProps {
  message: string;
  icon: React.ReactNode;
  children?: React.ReactNode;
  containerClassName?: string;
  className?: string;
}

interface ILiveAgentOnlyBannerProps {
  isLiveAgentTyping: boolean;
  liveAgentState: LiveAgentConnectionState;
  onClose?: () => void;
}

interface ICopilotLiveAgentInProgressBannerProps {
  onCancelClick?: () => void;
}

interface ICopilotLiveAgentConnectedBannerProps {
  isLiveAgentTyping?: boolean;
}

interface ICopilotLiveAgentBannerProps {
  liveAgentState: LiveAgentConnectionState;
  onCancelClick?: () => void;
  isLiveAgentTyping?: boolean;
}

const useLiveAgentBannerStyles = makeStyles({
  bannerContainer: {
    width: '100%',
    height: tokens.lineHeightHero700,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: '0 4px 8px 0 rgba(0,0,0, 0.2)',
    zIndex: 2,
  },
  banner: {
    alignItems: 'center',
    display: 'flex',
    width: '100%',
    height: tokens.lineHeightHero700,
    flexDirection: 'column',
  },
  bannerActiveContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    paddingTop: tokens.spacingVerticalSNudge,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
  },
  inProgressContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    flexDirection: 'column',
  },
  inProgress: {
    justifyContent: 'space-between',
    paddingTop: tokens.spacingVerticalXS,
  },
  progressBar: {
    width: '100%',
    height: '3px',
  },
  largeBannerContainer: {
    height: '48px',
    marginBottom: tokens.spacingVerticalL,
    boxShadow: 'none',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke1,
  },
  largeBanner: {
    paddingTop: tokens.spacingHorizontalM,
  },
});

const LiveAgentBannerBase: React.FC<ILiveAgentBannerBaseProps> = ({
  message, icon, children, containerClassName, className,
}) => {
  const i18n = useI18nContext()!;
  const styles = useLiveAgentBannerStyles();

  return (
    <div className={mergeClasses('live-agent-banner-container', styles.bannerContainer, containerClassName)}>
      <div className={mergeClasses('live-agent-banner', styles.banner)}>
        <div className={mergeClasses(styles.bannerActiveContainer, className)}>
          {icon}
          <Label size="small">{i18n.getString(message)}</Label>
          {children}
        </div>
      </div>
    </div>
  );
};

export const LiveAgentBanner: React.FC<ILiveAgentOnlyBannerProps> = ({ isLiveAgentTyping, liveAgentState, onClose }) => {
  const styles = useLiveAgentBannerStyles();

  const message = useMemo(() => {
    if (liveAgentState === LiveAgentConnectionState.IN_PROGRESS) {
      return _TL_('Connecting to an agent...');
    }
    if (isLiveAgentTyping) {
      return _TL_('Agent is typing...');
    }
    return _TL_('Chat with live agent');
  }, [liveAgentState, isLiveAgentTyping]);

  return (
    <LiveAgentBannerBase
      message={message}
      icon={<PersonSupport24Regular style={{ marginRight: 8 }} />}
      containerClassName={styles.largeBannerContainer}
      className={styles.largeBanner}
    >
      <Button
        style={{ position: 'absolute', right: 16 }}
        className="close-button"
        title={_TL_('Close')}
        aria-label={_TL_('Close Live agent chat')}
        icon={<Dismiss24Regular />}
        appearance="transparent"
        onClick={onClose}
      />
    </LiveAgentBannerBase>
  );
};

const CopilotLiveAgentInProgressBanner: React.FC<ICopilotLiveAgentInProgressBannerProps> = ({ onCancelClick }) => {
  const i18n = useI18nContext();
  const styles = useLiveAgentBannerStyles();
  return (
    <div className={styles.inProgressContainer}>
      <LiveAgentBannerBase
        message={_TL_('Please wait while we connect you to an agent')}
        icon={null}
        className={styles.inProgress}
      >
        <Tooltip content={_TL_('Stop connecting')} relationship="label">
          <Button appearance="transparent" icon={<SquareDismiss20Regular />} onClick={onCancelClick} />
        </Tooltip>
      </LiveAgentBannerBase>
      <ProgressBar
        className={styles.progressBar}
        aria-label={i18n?.getString(_TL_('Connecting to agent in progress'))}
      />
    </div>
  );
};

const CopilotLiveAgentConnectedBanner: React.FC<ICopilotLiveAgentConnectedBannerProps> = ({ isLiveAgentTyping }) => (
  <LiveAgentBannerBase
    message={isLiveAgentTyping ? _TL_('Agent is typing...') : _TL_('You are talking to a live agent')}
    icon={<CheckmarkCircle20Filled color="#107C10" style={{ marginRight: 8 }} />}
  />
);

export const CopilotLiveAgentBanner: React.FC<ICopilotLiveAgentBannerProps> = ({ liveAgentState, onCancelClick, isLiveAgentTyping }) => {
  switch (liveAgentState) {
    case LiveAgentConnectionState.IN_PROGRESS:
      return <CopilotLiveAgentInProgressBanner onCancelClick={onCancelClick} />;
    case LiveAgentConnectionState.CONNECTED:
      return <CopilotLiveAgentConnectedBanner isLiveAgentTyping={isLiveAgentTyping} />;
    default:
      return null;
  }
};
